import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { 
  Database, Plus, Edit2, Trash2, ChevronLeft, ChevronDown, ChevronRight,
  Loader2, Save, X, Settings, Monitor, Tag, Gamepad2, ArrowUp, ArrowDown, Palette, Crown, Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import SystemBarsConfig from "@/components/SystemBarsConfig";

const API = `${getApiUrl()}/api`;

export default function MasterDatabaseConfig() {
  const navigate = useNavigate();
  
  // Data state
  const [platforms, setPlatforms] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [expandedPlatforms, setExpandedPlatforms] = useState({});
  
  // Dialog states
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [systemDialogOpen, setSystemDialogOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [genreDialogOpen, setGenreDialogOpen] = useState(false);
  
  // Form states
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [editingSystem, setEditingSystem] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [editingGenre, setEditingGenre] = useState(null);
  const [selectedPlatformId, setSelectedPlatformId] = useState(null);
  const [selectedSystemId, setSelectedSystemId] = useState(null);
  
  // New item forms
  const [newPlatform, setNewPlatform] = useState({ name: "", slug: "" });

  // Subscription kill switch
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [subToggleLoading, setSubToggleLoading] = useState(false);
  const [newSystem, setNewSystem] = useState({ name: "", slug: "" });
  const [newTag, setNewTag] = useState({ name: "", description: "" });
  const [newGenre, setNewGenre] = useState({ name: "", slug: "", color: "#A1A1AA" });
  
  // Fetch data
  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/master/platforms`);
      setPlatforms(response.data);
      // Expand all platforms by default
      const expanded = {};
      response.data.forEach(p => { expanded[p.id] = true; });
      setExpandedPlatforms(expanded);
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
      toast.error("Failed to load platforms");
    }
  };
  
  const fetchContentTags = async () => {
    try {
      const response = await axios.get(`${API}/master/content-tags`);
      setContentTags(response.data);
    } catch (error) {
      console.error("Failed to fetch content tags:", error);
    }
  };
  
  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/master/genres`);
      setGenres(response.data);
    } catch (error) {
      console.error("Failed to fetch genres:", error);
    }
  };
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPlatforms(), fetchContentTags(), fetchGenres()]);
      // Fetch subscription toggle state
      try {
        const subRes = await axios.get(`${API}/subscription/admin/toggle`);
        setSubscriptionEnabled(subRes.data.subscriptionEnabled);
      } catch {}
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSubscriptionToggle = async () => {
    setSubToggleLoading(true);
    try {
      const newValue = !subscriptionEnabled;
      await axios.post(`${API}/subscription/admin/toggle`, { enabled: newValue });
      setSubscriptionEnabled(newValue);
      toast.success(`Subscription paywall ${newValue ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to toggle subscription");
    } finally {
      setSubToggleLoading(false);
    }
  };
  
  // Platform CRUD
  const handleCreatePlatform = async () => {
    if (!newPlatform.name.trim()) {
      toast.error("Platform name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", newPlatform.name);
      if (newPlatform.slug) params.append("slug", newPlatform.slug);
      
      await axios.post(`${API}/master/platforms?${params}`);
      toast.success("Platform created");
      setPlatformDialogOpen(false);
      setNewPlatform({ name: "", slug: "" });
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create platform");
    }
  };
  
  const handleUpdatePlatform = async () => {
    if (!editingPlatform?.name?.trim()) {
      toast.error("Platform name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", editingPlatform.name);
      if (editingPlatform.slug) params.append("slug", editingPlatform.slug);
      // Always send iconUrl so it can be set OR cleared (empty string).
      params.append("iconUrl", editingPlatform.iconUrl || "");
      
      await axios.put(`${API}/master/platforms/${editingPlatform.id}?${params}`);
      toast.success("Platform updated");
      setPlatformDialogOpen(false);
      setEditingPlatform(null);
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update platform");
    }
  };

  // Upload a platform/system logo to Cloudinary via the backend signature flow.
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const handleLogoUpload = async (file, onDone) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const sig = (await axios.get(`${API}/cloudinary/signature?folder=gamepop/platforms`)).data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      if (sig.transformation) fd.append("transformation", sig.transformation);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      onDone(data.secure_url);
      toast.success("Logo uploaded — remember to Save");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const handleDeletePlatform = async (platformId) => {
    if (!window.confirm("Delete this platform and all its systems and content tags?")) return;
    try {
      await axios.delete(`${API}/master/platforms/${platformId}`);
      toast.success("Platform deleted");
      fetchPlatforms();
      fetchContentTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete platform");
    }
  };
  
  // System CRUD
  const handleCreateSystem = async () => {
    if (!newSystem.name.trim()) {
      toast.error("System name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", newSystem.name);
      if (newSystem.slug) params.append("slug", newSystem.slug);
      
      await axios.post(`${API}/master/platforms/${selectedPlatformId}/systems?${params}`);
      toast.success("System created");
      setSystemDialogOpen(false);
      setNewSystem({ name: "", slug: "" });
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create system");
    }
  };
  
  const handleUpdateSystem = async () => {
    if (!editingSystem?.name?.trim()) {
      toast.error("System name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", editingSystem.name);
      if (editingSystem.slug) params.append("slug", editingSystem.slug);
      params.append("iconUrl", editingSystem.iconUrl || "");
      
      await axios.put(`${API}/master/platforms/${selectedPlatformId}/systems/${editingSystem.id}?${params}`);
      toast.success("System updated");
      setSystemDialogOpen(false);
      setEditingSystem(null);
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update system");
    }
  };
  
  const handleDeleteSystem = async (platformId, systemId) => {
    if (!window.confirm("Delete this system and all its content tags?")) return;
    try {
      await axios.delete(`${API}/master/platforms/${platformId}/systems/${systemId}`);
      toast.success("System deleted");
      fetchPlatforms();
      fetchContentTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete system");
    }
  };
  
  // Reorder system (move up/down in hierarchy)
  const handleReorderSystem = async (platformId, systemId, direction) => {
    const platform = platforms.find(p => p.id === platformId);
    if (!platform) return;
    
    const systems = [...(platform.systems || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const currentIndex = systems.findIndex(s => s.id === systemId);
    
    if (currentIndex === -1) return;
    if (direction === "up" && currentIndex === 0) return;
    if (direction === "down" && currentIndex === systems.length - 1) return;
    
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    
    // Swap sort orders
    const currentSortOrder = systems[currentIndex].sortOrder || currentIndex;
    const swapSortOrder = systems[swapIndex].sortOrder || swapIndex;
    
    try {
      // Update current system
      await axios.put(
        `${API}/master/platforms/${platformId}/systems/${systemId}?sortOrder=${swapSortOrder}`
      );
      // Update swapped system
      await axios.put(
        `${API}/master/platforms/${platformId}/systems/${systems[swapIndex].id}?sortOrder=${currentSortOrder}`
      );
      toast.success("System order updated");
      fetchPlatforms();
    } catch (error) {
      toast.error("Failed to reorder system");
    }
  };
  
  // Content Tag CRUD
  const handleCreateTag = async () => {
    if (!newTag.name.trim()) {
      toast.error("Tag name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", newTag.name);
      params.append("systemId", selectedSystemId);
      if (newTag.description) params.append("description", newTag.description);
      
      await axios.post(`${API}/master/content-tags?${params}`);
      toast.success("Content tag created");
      setTagDialogOpen(false);
      setNewTag({ name: "", description: "" });
      fetchContentTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create content tag");
    }
  };
  
  const handleUpdateTag = async () => {
    if (!editingTag?.name?.trim()) {
      toast.error("Tag name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", editingTag.name);
      if (editingTag.description !== undefined) params.append("description", editingTag.description || "");
      
      await axios.put(`${API}/master/content-tags/${editingTag.id}?${params}`);
      toast.success("Content tag updated");
      setTagDialogOpen(false);
      setEditingTag(null);
      fetchContentTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update content tag");
    }
  };
  
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm("Delete this content tag? It will be removed from all games.")) return;
    try {
      await axios.delete(`${API}/master/content-tags/${tagId}`);
      toast.success("Content tag deleted");
      fetchContentTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete content tag");
    }
  };
  
  // Genre CRUD
  const handleCreateGenre = async () => {
    if (!newGenre.name.trim()) {
      toast.error("Genre name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", newGenre.name);
      if (newGenre.slug) params.append("slug", newGenre.slug);
      if (newGenre.color) params.append("color", newGenre.color);
      
      await axios.post(`${API}/master/genres?${params}`);
      toast.success("Genre created");
      setGenreDialogOpen(false);
      setNewGenre({ name: "", slug: "", color: "#A1A1AA" });
      fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create genre");
    }
  };
  
  const handleUpdateGenre = async () => {
    if (!editingGenre?.name?.trim()) {
      toast.error("Genre name is required");
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append("name", editingGenre.name);
      if (editingGenre.slug) params.append("slug", editingGenre.slug);
      if (editingGenre.color) params.append("color", editingGenre.color);
      
      await axios.put(`${API}/master/genres/${editingGenre.id}?${params}`);
      toast.success("Genre updated");
      setGenreDialogOpen(false);
      setEditingGenre(null);
      fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update genre");
    }
  };
  
  const handleDeleteGenre = async (genreId) => {
    if (!window.confirm("Delete this genre? It will be removed from all games.")) return;
    try {
      await axios.delete(`${API}/master/genres/${genreId}`);
      toast.success("Genre deleted");
      fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete genre");
    }
  };
  
  const handleMoveGenre = async (genreId, direction) => {
    const currentIndex = genres.findIndex(g => g.id === genreId);
    if (currentIndex === -1) return;
    if (direction === "up" && currentIndex === 0) return;
    if (direction === "down" && currentIndex === genres.length - 1) return;
    
    // Create new order
    const newOrder = [...genres];
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    [newOrder[currentIndex], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[currentIndex]];
    
    try {
      await axios.post(`${API}/master/genres/reorder`, newOrder.map(g => g.id));
      fetchGenres();
    } catch (error) {
      toast.error("Failed to reorder genres");
    }
  };
  
  // Get tags for a specific system
  const getTagsForSystem = (systemId) => {
    return contentTags.filter(t => t.systemId === systemId);
  };
  
  // Find system name by ID
  const getSystemName = (systemId) => {
    for (const platform of platforms) {
      const system = platform.systems?.find(s => s.id === systemId);
      if (system) return system.name;
    }
    return "Unknown";
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/master-database")}
            className="text-white/60 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Settings className="w-8 h-8 text-purple-400" />
              Configuration
            </h1>
            <p className="text-white/60 mt-1">
              Manage platforms, systems, and content tags
            </p>
          </div>
        </div>
      </div>
      
      {/* Platforms & Systems */}
      <Card className="bg-[#0A0A0A] border-white/10 mb-6">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5" />
              Platforms & Systems
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                setEditingPlatform(null);
                setNewPlatform({ name: "", slug: "" });
                setPlatformDialogOpen(true);
              }}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="add-platform-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Platform
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-4">
            {platforms.map((platform) => (
              <Collapsible
                key={platform.id}
                open={expandedPlatforms[platform.id]}
                onOpenChange={(open) => setExpandedPlatforms({...expandedPlatforms, [platform.id]: open})}
              >
                <div className="rounded-lg border border-white/10 bg-white/5">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5">
                      <div className="flex items-center gap-3">
                        {expandedPlatforms[platform.id] ? (
                          <ChevronDown className="w-4 h-4 text-white/50" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/50" />
                        )}
                        <div>
                          <p className="font-medium text-white">{platform.name}</p>
                          <p className="text-xs text-white/50">
                            {platform.systems?.length || 0} systems • slug: {platform.slug}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedPlatformId(platform.id);
                            setEditingSystem(null);
                            setNewSystem({ name: "", slug: "" });
                            setSystemDialogOpen(true);
                          }}
                          className="text-green-400 hover:text-green-300 h-8"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          System
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingPlatform(platform);
                            setPlatformDialogOpen(true);
                          }}
                          className="text-white/50 hover:text-white h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePlatform(platform.id)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-t border-white/10 p-4 space-y-3">
                      <p className="text-xs text-white/40 mb-2">
                        ⬆️ Lower order = earlier/older system. When importing games, the earliest system is shown in the library.
                      </p>
                      {platform.systems?.length === 0 ? (
                        <p className="text-white/40 text-sm text-center py-2">No systems yet</p>
                      ) : (
                        [...(platform.systems || [])]
                          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                          .map((system, idx, arr) => (
                          <div key={system.id} className="rounded-lg bg-black/30 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="flex flex-col gap-0.5">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleReorderSystem(platform.id, system.id, "up")}
                                    disabled={idx === 0}
                                    className="h-5 w-5 p-0 text-white/30 hover:text-white disabled:opacity-20"
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleReorderSystem(platform.id, system.id, "down")}
                                    disabled={idx === arr.length - 1}
                                    className="h-5 w-5 p-0 text-white/30 hover:text-white disabled:opacity-20"
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </Button>
                                </div>
                                <span className="text-xs text-white/30 w-6 text-center">#{idx + 1}</span>
                                <Monitor className="w-4 h-4 text-blue-400" />
                                <span className="font-medium text-white">{system.name}</span>
                                <span className="text-xs text-white/40">({system.slug})</span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedPlatformId(platform.id);
                                    setSelectedSystemId(system.id);
                                    setEditingTag(null);
                                    setNewTag({ name: "", description: "" });
                                    setTagDialogOpen(true);
                                  }}
                                  className="text-amber-400 hover:text-amber-300 h-7 text-xs"
                                >
                                  <Tag className="w-3 h-3 mr-1" />
                                  Add Tag
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedPlatformId(platform.id);
                                    setEditingSystem(system);
                                    setSystemDialogOpen(true);
                                  }}
                                  className="text-white/50 hover:text-white h-7 w-7 p-0"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteSystem(platform.id, system.id)}
                                  className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Content tags for this system */}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {getTagsForSystem(system.id).map((tag) => (
                                <div
                                  key={tag.id}
                                  className="flex items-center gap-1 px-2 py-1 rounded bg-amber-500/20 text-amber-400 text-xs group"
                                >
                                  <Tag className="w-3 h-3" />
                                  <span>{tag.name}</span>
                                  <button
                                    onClick={() => {
                                      setSelectedSystemId(system.id);
                                      setEditingTag(tag);
                                      setTagDialogOpen(true);
                                    }}
                                    className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTag(tag.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              {getTagsForSystem(system.id).length === 0 && (
                                <span className="text-white/30 text-xs">No content tags</span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
            
            {platforms.length === 0 && (
              <div className="text-center py-8 text-white/50">
                <Gamepad2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No platforms configured</p>
                <p className="text-sm mt-1">Add a platform to get started</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Genres moved to Tags & Metadata page — see /app/master-database/tags */}

      {/* System Bars Configuration */}
      <SystemBarsConfig />

      {/* Subscription Paywall Toggle */}
      <Card className="bg-[#0A0A0A] border-purple-500/20">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-purple-400" />
            Subscription Paywall
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Pro Subscription Gate</p>
              <p className="text-zinc-500 text-sm mt-0.5">
                {subscriptionEnabled
                  ? "Paywall is ACTIVE — free users see upgrade prompts on Pro features"
                  : "Paywall is OFF — all features unlocked for everyone (Pre-Beta mode)"}
              </p>
            </div>
            <Button
              onClick={handleSubscriptionToggle}
              disabled={subToggleLoading}
              variant="outline"
              className={subscriptionEnabled
                ? "border-green-500/30 text-green-400 hover:bg-green-500/10"
                : "border-zinc-600 text-zinc-400 hover:bg-zinc-800"}
              data-testid="subscription-toggle-btn"
            >
              {subToggleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {subscriptionEnabled ? "Enabled" : "Disabled"}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Platform Dialog */}
      <Dialog open={platformDialogOpen} onOpenChange={setPlatformDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPlatform ? "Edit Platform" : "Add Platform"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={editingPlatform ? editingPlatform.name : newPlatform.name}
                onChange={(e) => editingPlatform 
                  ? setEditingPlatform({...editingPlatform, name: e.target.value})
                  : setNewPlatform({...newPlatform, name: e.target.value})
                }
                placeholder="e.g., Nintendo"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (auto-generated if empty)</Label>
              <Input
                value={editingPlatform ? editingPlatform.slug : newPlatform.slug}
                onChange={(e) => editingPlatform 
                  ? setEditingPlatform({...editingPlatform, slug: e.target.value})
                  : setNewPlatform({...newPlatform, slug: e.target.value})
                }
                placeholder="e.g., nintendo"
                className="bg-black/50 border-white/10"
              />
            </div>
            {editingPlatform && (
              <div className="space-y-2" data-testid="platform-logo-field">
                <Label>Logo image</Label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-black/50 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                    {editingPlatform.iconUrl ? (
                      <img src={editingPlatform.iconUrl} alt="" className="w-full h-full object-contain" data-testid="platform-logo-preview" />
                    ) : (
                      <Gamepad2 className="w-6 h-6 text-white/30" />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="platform-logo-input"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0], (url) => setEditingPlatform((prev) => ({ ...prev, iconUrl: url })))}
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
                        {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {editingPlatform.iconUrl ? "Replace" : "Upload"}
                      </span>
                    </label>
                    {editingPlatform.iconUrl && (
                      <button
                        type="button"
                        onClick={() => setEditingPlatform({ ...editingPlatform, iconUrl: "" })}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-red-300 hover:bg-red-500/10 border border-red-500/20"
                        data-testid="platform-logo-remove"
                      >
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-white/30 text-xs">Shown as this platform's badge across the app. Square PNG with transparent background works best. Leave empty for a generic icon.</p>
              </div>
            )}
            <Button 
              onClick={editingPlatform ? handleUpdatePlatform : handleCreatePlatform}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              {editingPlatform ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingPlatform ? "Save Changes" : "Create Platform"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* System Dialog */}
      <Dialog open={systemDialogOpen} onOpenChange={setSystemDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSystem ? "Edit System" : "Add System"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={editingSystem ? editingSystem.name : newSystem.name}
                onChange={(e) => editingSystem 
                  ? setEditingSystem({...editingSystem, name: e.target.value})
                  : setNewSystem({...newSystem, name: e.target.value})
                }
                placeholder="e.g., Nintendo Switch"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (auto-generated if empty)</Label>
              <Input
                value={editingSystem ? editingSystem.slug : newSystem.slug}
                onChange={(e) => editingSystem 
                  ? setEditingSystem({...editingSystem, slug: e.target.value})
                  : setNewSystem({...newSystem, slug: e.target.value})
                }
                placeholder="e.g., switch"
                className="bg-black/50 border-white/10"
              />
            </div>
            {editingSystem && (
              <div className="space-y-2" data-testid="system-logo-field">
                <Label>Logo image</Label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-black/50 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                    {editingSystem.iconUrl ? (
                      <img src={editingSystem.iconUrl} alt="" className="w-full h-full object-contain" data-testid="system-logo-preview" />
                    ) : (
                      <Gamepad2 className="w-6 h-6 text-white/30" />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="system-logo-input"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0], (url) => setEditingSystem((prev) => ({ ...prev, iconUrl: url })))}
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
                        {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {editingSystem.iconUrl ? "Replace" : "Upload"}
                      </span>
                    </label>
                    {editingSystem.iconUrl && (
                      <button
                        type="button"
                        onClick={() => setEditingSystem({ ...editingSystem, iconUrl: "" })}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-red-300 hover:bg-red-500/10 border border-red-500/20"
                        data-testid="system-logo-remove"
                      >
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-white/30 text-xs">Shown next to this system's badge. Square PNG with transparent background works best. Leave empty for text-only.</p>
              </div>
            )}
            <Button 
              onClick={editingSystem ? handleUpdateSystem : handleCreateSystem}
              className="w-full bg-blue-600 hover:bg-blue-500"
            >
              {editingSystem ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingSystem ? "Save Changes" : "Create System"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Content Tag Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTag ? "Edit Content Tag" : "Add Content Tag"}
              {selectedSystemId && !editingTag && (
                <span className="text-sm font-normal text-white/50 ml-2">
                  for {getSystemName(selectedSystemId)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={editingTag ? editingTag.name : newTag.name}
                onChange={(e) => editingTag 
                  ? setEditingTag({...editingTag, name: e.target.value})
                  : setNewTag({...newTag, name: e.target.value})
                }
                placeholder="e.g., Kinect Required"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={editingTag ? editingTag.description || "" : newTag.description}
                onChange={(e) => editingTag 
                  ? setEditingTag({...editingTag, description: e.target.value})
                  : setNewTag({...newTag, description: e.target.value})
                }
                placeholder="e.g., Requires Kinect sensor"
                className="bg-black/50 border-white/10"
              />
            </div>
            <Button 
              onClick={editingTag ? handleUpdateTag : handleCreateTag}
              className="w-full bg-amber-600 hover:bg-amber-500"
            >
              {editingTag ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingTag ? "Save Changes" : "Create Tag"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Genre Dialog */}
      <Dialog open={genreDialogOpen} onOpenChange={setGenreDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGenre ? "Edit Genre" : "Add Genre"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={editingGenre ? editingGenre.name : newGenre.name}
                onChange={(e) => editingGenre 
                  ? setEditingGenre({...editingGenre, name: e.target.value})
                  : setNewGenre({...newGenre, name: e.target.value})
                }
                placeholder="e.g., Action RPG"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (auto-generated if empty)</Label>
              <Input
                value={editingGenre ? editingGenre.slug : newGenre.slug}
                onChange={(e) => editingGenre 
                  ? setEditingGenre({...editingGenre, slug: e.target.value})
                  : setNewGenre({...newGenre, slug: e.target.value})
                }
                placeholder="e.g., action-rpg"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={editingGenre ? editingGenre.color : newGenre.color}
                  onChange={(e) => editingGenre 
                    ? setEditingGenre({...editingGenre, color: e.target.value})
                    : setNewGenre({...newGenre, color: e.target.value})
                  }
                  className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10"
                />
                <Input
                  value={editingGenre ? editingGenre.color : newGenre.color}
                  onChange={(e) => editingGenre 
                    ? setEditingGenre({...editingGenre, color: e.target.value})
                    : setNewGenre({...newGenre, color: e.target.value})
                  }
                  placeholder="#A1A1AA"
                  className="bg-black/50 border-white/10 flex-1"
                />
              </div>
            </div>
            <Button 
              onClick={editingGenre ? handleUpdateGenre : handleCreateGenre}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              {editingGenre ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingGenre ? "Save Changes" : "Create Genre"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
